import { useEffect, useId, useRef, useState } from "react";

export const STATUS_STYLES = {
  healthy: { dot: "bg-[#22C55E]", text: "text-[#16A34A]", chip: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]" },
  online: { dot: "bg-[#22C55E]", text: "text-[#16A34A]", chip: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]" },
  completed: { dot: "bg-[#22C55E]", text: "text-[#16A34A]", chip: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]" },
  active: { dot: "bg-[#22C55E]", text: "text-[#16A34A]", chip: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]" },
  warning: { dot: "bg-[#F59E0B]", text: "text-[#D97706]", chip: "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#D97706]" },
  "needs attention": { dot: "bg-[#F59E0B]", text: "text-[#D97706]", chip: "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#D97706]" },
  "pending approval": { dot: "bg-[#F59E0B]", text: "text-[#D97706]", chip: "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#D97706]" },
  new: { dot: "bg-sm-accent", text: "text-sm-primary", chip: "border-sm-accent/40 bg-sm-accent/15 text-sm-primary" },
  "approval required": { dot: "bg-[#F59E0B]", text: "text-[#D97706]", chip: "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#D97706]" },
  "in progress": { dot: "bg-sm-accent", text: "text-sm-primary", chip: "border-sm-accent/30 bg-sm-accent/10 text-sm-primary" },
  failed: { dot: "bg-[#EF4343]", text: "text-[#EF4343]", chip: "border-[#EF4343]/30 bg-[#EF4343]/10 text-[#EF4343]" },
  "on roadmap": { dot: "bg-[#8B5CF6]", text: "text-[#7C3AED]", chip: "border-[#8B5CF6]/30 bg-[#8B5CF6]/10 text-[#7C3AED]" },
  "under review": { dot: "bg-[#F59E0B]", text: "text-[#D97706]", chip: "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#D97706]" },
  submitted: { dot: "bg-sm-accent", text: "text-sm-primary", chip: "border-sm-accent/30 bg-sm-accent/10 text-sm-primary" },
  shipped: { dot: "bg-[#22C55E]", text: "text-[#16A34A]", chip: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]" },
  declined: { dot: "bg-[#EF4343]", text: "text-[#EF4343]", chip: "border-[#EF4343]/30 bg-[#EF4343]/10 text-[#EF4343]" },
  info: { dot: "bg-sm-accent", text: "text-sm-primary", chip: "border-sm-accent/30 bg-sm-accent/10 text-sm-primary" },
};

const ROLE_LABELS = {
  owner: "Owner/Admin",
  admin: "Owner/Admin",
  owner_admin: "Owner/Admin",
  publisher: "Publisher",
  drafter: "Drafter",
  viewer: "Viewer",
};

export function formatRoleLabel(role) {
  const normalized = String(role ?? "").trim().toLowerCase();
  if (!normalized) return "Role not reported";
  return ROLE_LABELS[normalized] ?? normalized.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export const StatusBadge = ({ status, pulse = false, className = "", testId }) => {
  const s = STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.info;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.chip} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot} ${pulse ? "node-pulse" : ""}`} />
      {status}
    </span>
  );
};

export const StatusDot = ({ status, pulse = false }) => {
  const s = STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.info;
  return <span className={`inline-block h-2 w-2 rounded-full ${s.dot} ${pulse ? "node-pulse" : ""}`} />;
};

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`label-eyebrow text-sm-muted ${className}`}>{children}</div>
);

export const HelpTip = ({ label = "Why am I seeing this?", children, className = "" }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <span ref={containerRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-sm-ink/15 bg-white text-[11px] font-bold text-sm-ink/55 hover:border-sm-accent/45 hover:text-sm-primary"
      >
        ?
      </button>
      {open ? (
        <span id={panelId} role="note" className="absolute right-0 top-8 z-50 w-72 rounded-xl border border-sm-ink/10 bg-white p-4 text-left text-xs font-normal leading-relaxed text-sm-ink/65 shadow-xl">
          {children}
        </span>
      ) : null}
    </span>
  );
};

export const PageHeader = ({ title, subtitle, children, testId }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between" data-testid={testId}>
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-sm-ink md:text-4xl">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm text-sm-ink/70 md:text-base">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-3">{children}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, description }) => (
  <div data-testid="empty-state" className="flex flex-col items-center justify-center rounded-xl border border-dashed border-sm-ink/15 py-16 text-center">
    {Icon && <Icon className="h-8 w-8 text-sm-ink/30" />}
    <div className="mt-3 font-display text-lg font-semibold text-sm-ink/80">{title}</div>
    <p className="mt-1 max-w-sm text-sm text-sm-ink/50">{description}</p>
  </div>
);
